"use client";

import Image from "next/image";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import type { SVGProps } from "react";
import { useTranslations } from "next-intl";
import { uploadFile } from "@/services/file-manager-service";
import { verifyActions, type VerifyActionsResult } from "@/services/action-service";
import { ApiRequestError } from "@/lib/fetcher";
import { DUPLICATE_ERROR, isDuplicateError } from "@/lib/duplicate-errors";
import { domId } from "@/lib/dom-id";
import { Button, Input, Textarea } from "@/components/ui";
import FilterDropdown from "@/components/filter-dropdown";
import { InlineStatus } from "@/components/ui/inline-status";

export type UploadMethod = "file" | "text" | "url";
type ProcessingPhase = "validating" | "enhancing";
type UploadErrorKind = "validation" | "upload" | "processing";
type UploadErrorCode =
  | "uploadFailed"
  | "invalidFileType"
  | "fileTooLarge"
  | "emptyFile"
  | "uploadFirst"
  | "enterUrl"
  | "invalidUrl"
  | "enterContent"
  | "noEndpointsFile"
  | "noEndpointsText"
  | "noEndpointsUrl"
  | "invalidSpec"
  | "connectorNameExists"
  | "fileUnreadable"
  | "urlUnreadable"
  | "accessUnavailable"
  | "requestUnavailable"
  | "processingFailed";

const ACCEPTED_EXTENSIONS = [
  ".doc",
  ".docx",
  ".pdf",
  ".txt",
  ".yaml",
  ".yml",
  ".json",
  ".postman_collection.json",
] as const;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const BRAND_GRADIENT = "linear-gradient(90deg, #713D98 0%, #3E3D98 140.38%)";
const LOADER_TITLE_GRADIENT =
  "linear-gradient(186.49deg, #E742EB 14.103%, #6300FF 50.688%, #3D70F1 85.221%)";
const LOADER_SUBTITLE_GRADIENT =
  "linear-gradient(185.47deg, #4E5053 3.7323%, #E8E8E8 96.884%, #F2F2F2 96.893%)";

export interface UploadState {
  uploadMethod: UploadMethod;
  uploadedFileUrl: string;
  fileName: string;
  textContent: string;
  urlContent: string;
}

export interface ActionUploadModalProps {
  purpose: string;
  open?: boolean;
  onClose?: () => void;
  onSubmit: (data: {
    sourceType: "file" | "url" | "text";
    content: string;
    selectedServerUrl?: string;
    fileType?: string;
  }) => void | Promise<void>;
  submitLabel?: string;
  cancelLabel?: string;
  submitting?: boolean;
  submitAllowed?: boolean;
  asModal?: boolean;
  showHeader?: boolean;
  title?: string;
  subtitle?: string;
  headerVariant?: "default" | "connector-step";
  processingPhase?: ProcessingPhase | null;
  initialState?: Partial<UploadState>;
  onStateChange?: (state: UploadState) => void;
}

interface UploadErrorState {
  kind: UploadErrorKind;
  code: UploadErrorCode;
}

type FileValidationResult =
  | { valid: true }
  | { valid: false; code: UploadErrorCode };

function classifyProcessingError(
  error: unknown,
  method: UploadMethod,
): UploadErrorState {
  if (error instanceof ApiRequestError) {
    // The Add New Connector step creates the connector before processing, and
    // a taken name comes back from that create as a 409.
    if (isDuplicateError(error, DUPLICATE_ERROR.connectorName)) {
      return { kind: "validation", code: "connectorNameExists" };
    }
    if (error.errorCode === "NO_VALID_ENDPOINTS") {
      return {
        kind: "validation",
        code:
          method === "file"
            ? "noEndpointsFile"
            : method === "url"
              ? "noEndpointsUrl"
              : "noEndpointsText",
      };
    }
    if (method === "url" && error.errorCode === "INVALID_URL") {
      return { kind: "validation", code: "invalidUrl" };
    }
    if (error.httpStatus === 400) {
      if (method === "url" && error.errorCode === "URL_IMPORT_FAILED") {
        return { kind: "validation", code: "urlUnreadable" };
      }
      if (
        method === "file" &&
        [
          "FILE_PARSING_FAILED",
          "UNSUPPORTED_FILE_TYPE",
          "INVALID_FILE_EXTENSION",
        ].includes(error.errorCode || "")
      ) {
        return { kind: "validation", code: "fileUnreadable" };
      }
      if (error.errorCode === "VALIDATION_FAILED") {
        return { kind: "validation", code: "invalidSpec" };
      }
    }

    if (error.httpStatus === 401 || error.httpStatus === 403) {
      return { kind: "validation", code: "accessUnavailable" };
    }
    if (error.httpStatus === 404) {
      return { kind: "validation", code: "requestUnavailable" };
    }
    if (
      error.httpStatus === 408 ||
      error.httpStatus === 429 ||
      (error.httpStatus !== undefined && error.httpStatus >= 500)
    ) {
      return { kind: "processing", code: "processingFailed" };
    }
    if (error.httpStatus !== undefined && error.httpStatus >= 400) {
      return { kind: "validation", code: "invalidSpec" };
    }
  }
  if (error instanceof TypeError && /fetch|network|load failed/i.test(error.message)) {
    return { kind: "processing", code: "processingFailed" };
  }
  return { kind: "validation", code: "requestUnavailable" };
}

function UploadDropIllustration() {
  return (
    // Square at any height: the SVGs stretch to their box, so a drop zone
    // that shrinks (AIS-2673) scales the illustration instead of squashing it.
    <div className="relative aspect-square h-[100px] min-h-0 shrink" aria-hidden="true">
      <Image
        src="/figma/add-api-upload-illustration-base.svg"
        alt=""
        fill
        sizes="100px"
      />
      <Image
        src="/figma/add-api-upload-illustration-overlay.svg"
        alt=""
        fill
        sizes="100px"
      />
    </div>
  );
}

function UploadFileCardIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 16 19" fill="none" aria-hidden="true" {...props}>
      <path
        d="M14.0833 7.83333V4.75C14.0833 3.34987 14.0833 2.6498 13.8109 2.11502C13.5712 1.64462 13.1887 1.26217 12.7183 1.02248C12.1835 0.750001 11.4835 0.750001 10.0833 0.750001H4.75C3.34987 0.750001 2.6498 0.750001 2.11502 1.02248C1.64462 1.26217 1.26217 1.64462 1.02248 2.11502C0.750001 2.6498 0.750001 3.34987 0.750001 4.75V13.4167C0.750001 14.8168 0.750001 15.5169 1.02248 16.0516C1.26217 16.522 1.64462 16.9045 2.11502 17.1442C2.6498 17.4167 3.34987 17.4167 4.75 17.4167H7.41667M9.08333 8.25H4.08333M5.75 11.5833H4.08333M10.75 4.91667H4.08333M12.4167 16.5833V11.5833M9.91667 14.0833H14.9167"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function AddContentCardIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 15 19" fill="none" aria-hidden="true" {...props}>
      <path
        d="M5.75 9.5L4.08333 11.5833L5.75 13.6667M9.08333 9.5L10.75 11.5833L9.08333 13.6667M9.08333 0.75V4.08333C9.08333 4.52536 9.25893 4.94928 9.57149 5.26184C9.88405 5.57441 10.308 5.75 10.75 5.75H14.0833M9.91667 0.75H2.41667C1.97464 0.75 1.55072 0.925595 1.23816 1.23816C0.925595 1.55072 0.75 1.97464 0.75 2.41667V15.75C0.75 16.192 0.925595 16.6159 1.23816 16.9285C1.55072 17.2411 1.97464 17.4167 2.41667 17.4167H12.4167C12.8587 17.4167 13.2826 17.2411 13.5952 16.9285C13.9077 16.6159 14.0833 16.192 14.0833 15.75V4.91667L9.91667 0.75Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ImportUrlCardIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 19 10" fill="none" aria-hidden="true" {...props}>
      <path
        d="M6.58333 9.08333H4.91667C3.8116 9.08333 2.75179 8.64435 1.97039 7.86294C1.18899 7.08154 0.75 6.02174 0.75 4.91667C0.75 3.8116 1.18899 2.75179 1.97039 1.97039C2.75179 1.18899 3.8116 0.75 4.91667 0.75H6.58333M11.5833 0.75H13.25C14.3551 0.75 15.4149 1.18899 16.1963 1.97039C16.9777 2.75179 17.4167 3.8116 17.4167 4.91667C17.4167 6.02174 16.9777 7.08154 16.1963 7.86294C15.4149 8.64435 14.3551 9.08333 13.25 9.08333H11.5833M5.75 4.91667H12.4167"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function GradientCircleCheckIcon(props: SVGProps<SVGSVGElement>) {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 19 19" fill="none" aria-hidden="true" {...props}>
      <path
        d="M6.77083 9.27083L8.4375 10.9375L11.7708 7.60417M17.6042 9.27083C17.6042 13.8732 13.8732 17.6042 9.27083 17.6042C4.66846 17.6042 0.9375 13.8732 0.9375 9.27083C0.9375 4.66846 4.66846 0.9375 9.27083 0.9375C13.8732 0.9375 17.6042 4.66846 17.6042 9.27083Z"
        stroke={`url(#${gradientId})`}
        strokeWidth="1.875"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="0.9375"
          y1="9.27083"
          x2="24.3349"
          y2="9.27083"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#713D98" />
          <stop offset="1" stopColor="#3E3D98" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function UploadedFileStateIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 21 26" fill="none" aria-hidden="true" {...props}>
      <g>
        <path
          d="M13.7083 0.875H3.20833C2.58949 0.875 1.996 1.12083 1.55842 1.55842C1.12083 1.996 0.875 2.58949 0.875 3.20833V21.875C0.875 22.4938 1.12083 23.0873 1.55842 23.5249C1.996 23.9625 2.58949 24.2083 3.20833 24.2083H17.2083C17.8272 24.2083 18.4207 23.9625 18.8582 23.5249C19.2958 23.0873 19.5417 22.4938 19.5417 21.875V6.70833L13.7083 0.875Z"
          fill="#DCDFE3"
        />
        <path
          d="M12.5417 0.875V5.54167C12.5417 6.16051 12.7875 6.754 13.2251 7.19158C13.6627 7.62917 14.2562 7.875 14.875 7.875H19.5417"
          fill="#DCDFE3"
        />
        <path d="M5.54167 13.7083H7.875" fill="#DCDFE3" />
        <path d="M12.5417 13.7083H14.875" fill="#DCDFE3" />
        <path d="M5.54167 18.375H7.875" fill="#DCDFE3" />
        <path d="M12.5417 18.375H14.875" fill="#DCDFE3" />
        <path
          d="M12.5417 0.875V5.54167C12.5417 6.16051 12.7875 6.754 13.2251 7.19158C13.6627 7.62917 14.2562 7.875 14.875 7.875H19.5417M5.54167 13.7083H7.875M12.5417 13.7083H14.875M5.54167 18.375H7.875M12.5417 18.375H14.875M13.7083 0.875H3.20833C2.58949 0.875 1.996 1.12083 1.55842 1.55842C1.12083 1.996 0.875 2.58949 0.875 3.20833V21.875C0.875 22.4938 1.12083 23.0873 1.55842 23.5249C1.996 23.9625 2.58949 24.2083 3.20833 24.2083H17.2083C17.8272 24.2083 18.4207 23.9625 18.8582 23.5249C19.2958 23.0873 19.5417 22.4938 19.5417 21.875V6.70833L13.7083 0.875Z"
          stroke="#0B0809"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}

function EnhancingSparklesIcon(props: SVGProps<SVGSVGElement>) {
  const gradientId = useId();

  return (
    <svg viewBox="0 0 19 19" fill="none" aria-hidden="true" {...props}>
      <path
        d="M15.7488 1.58354V4.91687M17.4154 3.2502H14.0821M2.41545 13.2502V14.9169M3.24878 14.0835H1.58211M7.3629 12.0002C7.2885 11.7118 7.13818 11.4486 6.92758 11.238C6.71698 11.0274 6.45379 10.8771 6.1654 10.8027L1.0529 9.48437C0.965672 9.45962 0.888903 9.40708 0.83424 9.33475C0.779576 9.26241 0.75 9.17421 0.75 9.08354C0.75 8.99287 0.779576 8.90468 0.83424 8.83234C0.888903 8.76 0.965672 8.70747 1.0529 8.68271L6.1654 7.36354C6.45369 7.28921 6.71681 7.13902 6.9274 6.92857C7.13799 6.71813 7.28837 6.45511 7.3629 6.16687L8.68123 1.05437C8.70574 0.966806 8.75822 0.889659 8.83066 0.834703C8.90311 0.779747 8.99155 0.75 9.08248 0.75C9.17341 0.75 9.26185 0.779747 9.33429 0.834703C9.40674 0.889659 9.45922 0.966806 9.48373 1.05437L10.8012 6.16687C10.8756 6.45527 11.0259 6.71846 11.2365 6.92906C11.4471 7.13966 11.7103 7.28998 11.9987 7.36438L17.1112 8.68187C17.1991 8.70612 17.2767 8.75855 17.3319 8.83111C17.3872 8.90366 17.4171 8.99234 17.4171 9.08354C17.4171 9.17474 17.3872 9.26342 17.3319 9.33598C17.2767 9.40853 17.1991 9.46096 17.1112 9.48521L11.9987 10.8027C11.7103 10.8771 11.4471 11.0274 11.2365 11.238C11.0259 11.4486 10.8756 11.7118 10.8012 12.0002L9.4829 17.1127C9.45839 17.2003 9.40591 17.2774 9.33346 17.3324C9.26101 17.3873 9.17258 17.4171 9.08165 17.4171C8.99071 17.4171 8.90228 17.3873 8.82983 17.3324C8.75738 17.2774 8.7049 17.2003 8.6804 17.1127L7.3629 12.0002Z"
        stroke={`url(#${gradientId})`}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <defs>
        <linearGradient
          id={gradientId}
          x1="14.9412"
          y1="2.98435"
          x2="3.55806"
          y2="15.2733"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E742EB" />
          <stop offset="0.514423" stopColor="#6300FF" />
          <stop offset="1" stopColor="#3D70F1" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function EnhancingProcessingBadge() {
  return (
    <>
      <style jsx>{`
        @keyframes ai-enhance-orbit {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes ai-enhance-glow {
          0%,
          100% {
            opacity: 0.45;
            transform: scale(0.92);
          }
          50% {
            opacity: 0.85;
            transform: scale(1.08);
          }
        }

        @keyframes ai-enhance-float {
          0%,
          100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-2px) scale(1.04);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ai-enhance-orbit,
          .ai-enhance-glow,
          .ai-enhance-float {
            animation: none !important;
          }
        }
      `}</style>
      <div
        className="relative flex h-[49px] w-[49px] items-center justify-center"
        aria-hidden="true"
      >
        <div
          className="ai-enhance-orbit absolute inset-0 rounded-full border border-[#DDD9FF]"
          style={{ animation: "ai-enhance-orbit 3s linear infinite" }}
        />
        <div
          className="ai-enhance-glow absolute inset-[6px] rounded-full bg-[#E6E2FF] blur-[6px]"
          style={{ animation: "ai-enhance-glow 1.8s ease-in-out infinite" }}
        />
        <div
          className="ai-enhance-float relative flex h-9 w-9 items-center justify-center rounded-full bg-[#EEEEFB] p-2"
          style={{ animation: "ai-enhance-float 1.8s ease-in-out infinite" }}
        >
          <EnhancingSparklesIcon className="h-5 w-5" />
        </div>
      </div>
    </>
  );
}

function ValidatingLoaderIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 32 32" fill="none" aria-hidden="true" {...props}>
      <path
        d="M7.7405 4.20421C7.23366 3.48036 7.40628 2.47487 8.17711 2.04282C10.6411 0.661757 13.4361 -0.0471903 16.2792 0.00243817C19.6576 0.0614083 22.9306 1.18838 25.629 3.22184C28.3275 5.25529 30.313 8.09083 31.3009 11.3221C32.2888 14.5533 32.2283 18.0143 31.1283 21.2091C30.0282 24.4039 27.945 27.1684 25.1772 29.1064C22.4094 31.0445 19.0991 32.0565 15.7208 31.9976C12.3424 31.9386 9.06943 30.8116 6.37095 28.7782C4.09994 27.0668 2.33395 24.7874 1.24257 22.1821C0.90115 21.3671 1.38416 20.4685 2.22921 20.2101C3.07425 19.9518 3.95969 20.4331 4.32139 21.2393C5.20153 23.2012 6.56578 24.9181 8.29676 26.2225C10.4555 27.8493 13.0739 28.7509 15.7766 28.7981C18.4793 28.8452 21.1275 28.0356 23.3418 26.4852C25.556 24.9347 27.2226 22.7231 28.1026 20.1673C28.9827 17.6115 29.031 14.8426 28.2407 12.2576C27.4504 9.67267 25.862 7.40423 23.7032 5.77747C21.5444 4.15071 18.9261 3.24913 16.2234 3.20195C14.0563 3.16412 11.9242 3.67718 10.0228 4.68129C9.24138 5.09393 8.24734 4.92806 7.7405 4.20421Z"
        fill="#7674DD"
      />
    </svg>
  );
}

function ProcessingState({
  phase,
  description,
  purpose,
  title,
}: {
  phase: ProcessingPhase;
  description: string;
  purpose: string;
  title: string;
}) {
  if (phase === "enhancing") {
    return (
      <div
        className="flex h-[294px] w-full items-end justify-center"
        data-testid={domId("sec", `${purpose}-processing-state`)}
      >
        <div className="flex w-[475px] flex-col items-center gap-2 text-center">
          <EnhancingProcessingBadge />
          <div className="flex w-full flex-col items-center gap-1">
            <p
              className="w-full bg-clip-text text-[20px] font-semibold leading-[1.4] text-transparent"
              style={{ backgroundImage: LOADER_TITLE_GRADIENT }}
            >
              {title}
            </p>
            <p
              className="w-full bg-clip-text text-[16px] leading-[1.4] text-transparent"
              style={{ backgroundImage: LOADER_SUBTITLE_GRADIENT }}
            >
              {description}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex h-[294px] w-full items-end justify-center"
      data-testid={domId("sec", `${purpose}-processing-state`)}
    >
      <div className="flex w-[228px] flex-col items-center gap-2 text-center">
        <div
          className="relative flex h-8 w-8 items-center justify-center"
          aria-hidden="true"
        >
          <ValidatingLoaderIcon className="h-8 w-8 animate-[spin_1.1s_linear_infinite]" />
        </div>
        <div className="flex w-full flex-col items-center gap-1">
          <p
            className="w-full bg-clip-text text-[20px] font-semibold leading-[1.4] text-transparent"
            style={{ backgroundImage: LOADER_TITLE_GRADIENT }}
          >
            {title}
          </p>
          <p
            className="w-full bg-clip-text text-[16px] leading-[1.4] text-transparent"
            style={{ backgroundImage: LOADER_SUBTITLE_GRADIENT }}
          >
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function MethodCard({
  description,
  icon,
  id,
  selected,
  title,
  onClick,
}: {
  description: string;
  icon: React.ReactNode;
  id: string;
  selected: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <Button
      data-testid={id}
      type="button"
      variant="unstyled"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex h-full min-h-[112px] w-full flex-col rounded-[16px] border px-6 py-4 text-left transition-colors ${
        selected
          ? "border-[#713D98] bg-[#F4EBFD]"
          : "border-[#B2BBC4] bg-white hover:border-[#B2BBC4]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full text-white"
            style={{ backgroundImage: BRAND_GRADIENT }}
          >
            {icon}
          </div>
          <span
            className={`text-[16px] font-medium leading-[1.4] ${
              selected ? "bg-clip-text text-transparent" : "text-[#0B0809]"
            }`}
            style={selected ? { backgroundImage: BRAND_GRADIENT } : undefined}
          >
            {title}
          </span>
        </div>
        {selected ? (
          <GradientCircleCheckIcon className="mt-0.5 h-5 w-5 shrink-0" />
        ) : null}
      </div>
      <p
        className={`mt-4 text-[14px] leading-[1.4] ${
          selected ? "bg-clip-text text-transparent" : "text-[#444746]"
        }`}
        style={selected ? { backgroundImage: BRAND_GRADIENT } : undefined}
      >
        {description}
      </p>
    </Button>
  );
}

export default function ActionUploadModal({
  purpose,
  open = true,
  onClose,
  onSubmit,
  submitLabel,
  cancelLabel,
  submitting = false,
  submitAllowed = true,
  asModal = false,
  showHeader = true,
  title,
  subtitle,
  headerVariant = "default",
  processingPhase = null,
  initialState,
  onStateChange,
}: ActionUploadModalProps) {
  const t = useTranslations("ActionUploadModal");
  const basePurpose = purpose;
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>(
    initialState?.uploadMethod || "file",
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(
    initialState?.fileName ? ({ name: initialState.fileName } as File) : null,
  );
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string>(
    initialState?.uploadedFileUrl || "",
  );
  const [uploading, setUploading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [serverOptions, setServerOptions] = useState<
    VerifyActionsResult["server_selections"][number]["options"]
  >([]);
  const [selectedServerUrl, setSelectedServerUrl] = useState("");
  const [showServerError, setShowServerError] = useState(false);
  const busy = uploading || submitting || verifying;
  const [textContent, setTextContent] = useState(
    initialState?.textContent || "",
  );
  const [urlContent, setUrlContent] = useState(initialState?.urlContent || "");
  const [error, setError] = useState<UploadErrorState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  const errorId = useId();

  const resolvedSubmitLabel = submitLabel ?? t("actions.process");
  const resolvedCancelLabel = cancelLabel ?? t("actions.cancel");
  const resolvedTitle = title ?? t("title");
  const resolvedSubtitle = subtitle ?? t("subtitle");
  const errorMessages: Record<UploadErrorCode, string> = {
    uploadFailed: t("errors.uploadFailed"),
    invalidFileType: t("errors.invalidFileType", {
      formats: ACCEPTED_EXTENSIONS.join(", "),
    }),
    fileTooLarge: t("errors.fileTooLarge"),
    emptyFile: t("errors.emptyFile"),
    uploadFirst: t("errors.uploadFirst"),
    enterUrl: t("errors.enterUrl"),
    invalidUrl: t("errors.invalidUrl"),
    enterContent: t("errors.enterContent"),
    noEndpointsFile: t("errors.noEndpointsFile"),
    noEndpointsText: t("errors.noEndpointsText"),
    noEndpointsUrl: t("errors.noEndpointsUrl"),
    invalidSpec: t("errors.invalidSpec"),
    connectorNameExists: t("errors.connectorNameExists"),
    fileUnreadable: t("errors.fileUnreadable"),
    urlUnreadable: t("errors.urlUnreadable"),
    accessUnavailable: t("errors.accessUnavailable"),
    requestUnavailable: t("errors.requestUnavailable"),
    processingFailed: t("errors.processingFailed"),
  };

  useEffect(() => {
    if (onStateChange) {
      onStateChange({
        uploadMethod,
        uploadedFileUrl,
        fileName: selectedFile?.name || "",
        textContent,
        urlContent,
      });
    }
  }, [
    uploadMethod,
    uploadedFileUrl,
    selectedFile,
    textContent,
    urlContent,
    onStateChange,
  ]);

  // Start fresh once the parent actually closes the modal. Cancel only asks
  // to close: a parent that confirms unsaved changes may keep it open, and
  // "Keep editing" must return to the same method and input.
  useEffect(() => {
    if (open) return;
    setSelectedFile(null);
    setUploadedFileUrl("");
    setTextContent("");
    setUrlContent("");
    setError(null);
    setUploadMethod("file");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [open]);

  // Focus a new error so screen readers announce it, and bring it into view
  // right above the actions: it can land below the fold of a scrolling dialog
  // or under its sticky actions. "nearest" would skip an error that is inside
  // the scroll area but hidden by those actions.
  useEffect(() => {
    if (!error) return;
    errorRef.current?.focus({ preventScroll: true });
    errorRef.current?.scrollIntoView({ block: "end" });
  }, [error]);

  useEffect(() => {
    setServerOptions([]);
    setSelectedServerUrl("");
    setShowServerError(false);
  }, [uploadMethod, uploadedFileUrl, textContent, urlContent]);

  const canSubmit =
    submitAllowed &&
    // A validation error waits for corrected input; a transient processing
    // failure can simply be processed again.
    error?.kind !== "validation" &&
    ((uploadMethod === "file" && Boolean(uploadedFileUrl)) ||
      (uploadMethod === "url" && Boolean(urlContent.trim())) ||
      (uploadMethod === "text" && Boolean(textContent.trim())));

  const methodCards = useMemo(
    () => [
      {
        value: "file" as const,
        title: t("methods.uploadFile"),
        description: t("descriptions.uploadFile"),
        icon: <UploadFileCardIcon className="h-5 w-5" />,
      },
      {
        value: "text" as const,
        title: t("methods.addContent"),
        description: t("descriptions.addContent"),
        icon: <AddContentCardIcon className="h-5 w-5" />,
      },
      {
        value: "url" as const,
        title: t("methods.importUrl"),
        description: t("descriptions.importUrl"),
        icon: <ImportUrlCardIcon className="h-5 w-5" />,
      },
    ],
    [t],
  );

  const activeStatus = useMemo(() => {
    if (!processingPhase) return null;

    if (processingPhase === "enhancing") {
      return {
        phase: processingPhase,
        title: t("status.enhancing"),
        description: t("status.enhancingDescription"),
      };
    }

    return {
      phase: processingPhase,
      title: t("status.validating"),
      description: t("status.validatingDescription"),
    };
  }, [processingPhase, t]);

  if (asModal && !open) return null;

  if (activeStatus) {
    return (
      <ProcessingState
        phase={activeStatus.phase}
        purpose={basePurpose}
        title={activeStatus.title}
        description={activeStatus.description}
      />
    );
  }

  const resetUploadState = () => {
    setSelectedFile(null);
    setUploadedFileUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getFileExtension = (fileName: string) => {
    const normalized = fileName.toLowerCase();
    if (normalized.endsWith(".postman_collection.json")) {
      return ".postman_collection.json";
    }

    const extensionIndex = normalized.lastIndexOf(".");
    return extensionIndex >= 0 ? normalized.slice(extensionIndex) : "";
  };

  const validateSelectedFile = (file: File): FileValidationResult => {
    if (file.size === 0) {
      return {
        valid: false,
        code: "emptyFile",
      };
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      return {
        valid: false,
        code: "fileTooLarge",
      };
    }

    const extension = getFileExtension(file.name);
    const isAccepted =
      extension === ".postman_collection.json" ||
      ACCEPTED_EXTENSIONS.some((acceptedExtension) =>
        file.name.toLowerCase().endsWith(acceptedExtension),
      );

    if (!isAccepted) {
      return {
        valid: false,
        code: "invalidFileType",
      };
    }

    return { valid: true };
  };

  const uploadSelectedFile = async (file: File) => {
    const validation = validateSelectedFile(file);
    if (!validation.valid) {
      resetUploadState();
      setError({
        kind: "validation",
        code: validation.code,
      });
      return;
    }

    setSelectedFile(file);
    setUploadedFileUrl("");
    setUploading(true);
    setError(null);

    try {
      const result = await uploadFile(file, "api-specs");
      if (result.success && result.fileUrl) {
        setUploadedFileUrl(result.fileUrl);
        return;
      }

      throw new Error(result.error || t("errors.uploadFailed"));
    } catch (err) {
      console.error("File upload failed:", err);
      setError({
        kind: "upload",
        code: "uploadFailed",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose?.();
  };

  const handleRemoveSelectedFile = () => {
    resetUploadState();
    setError(null);
  };

  const submitCurrentState = async () => {
    if (!submitAllowed || busy) return;

    let sourceType: "file" | "url" | "text";
    let content: string;

    if (uploadMethod === "file") {
      if (!uploadedFileUrl) {
        setError({
          kind: "validation",
          code: "uploadFirst",
        });
        return;
      }
      sourceType = "file";
      content = uploadedFileUrl;
    } else if (uploadMethod === "url") {
      if (!urlContent.trim()) {
        setError({
          kind: "validation",
          code: "enterUrl",
        });
        return;
      }
      try {
        const importUrl = new URL(urlContent.trim());
        if (
          !["http:", "https:"].includes(importUrl.protocol) ||
          !importUrl.hostname
        ) {
          throw new Error("Unsupported URL");
        }
      } catch {
        setError({ kind: "validation", code: "invalidUrl" });
        return;
      }
      sourceType = "url";
      content = urlContent.trim();
    } else {
      if (!textContent.trim()) {
        setError({
          kind: "validation",
          code: "enterContent",
        });
        return;
      }
      sourceType = "text";
      content = textContent.trim();
    }

    setError(null);

    setVerifying(true);
    try {
      const fileType =
        sourceType === "file"
          ? getFileExtension(selectedFile?.name || "").slice(1)
          : undefined;
      const verification = await verifyActions({
        source_type: sourceType,
        content,
        file_type: fileType,
        ...(sourceType === "file" && selectedFile
          ? { file_size: selectedFile.size }
          : {}),
      });
      const documentSelection = verification?.server_selections.find(
        (selection) => selection.scope === "document",
      );
      const nextServerOptions = documentSelection?.options ?? [];
      const selectedServer =
        documentSelection?.selected_url ||
        (nextServerOptions.some((option) => option.url === selectedServerUrl)
          ? selectedServerUrl
          : "");
      setServerOptions(nextServerOptions);
      setSelectedServerUrl(selectedServer);
      if (documentSelection?.required && !selectedServer) {
        setShowServerError(serverOptions.length > 1);
        return;
      }
      setShowServerError(false);

      await onSubmit({
        sourceType,
        content,
        selectedServerUrl: selectedServer || undefined,
        fileType,
      });
    } catch (err) {
      console.error("API processing failed:", err);
      setError(classifyProcessingError(err, uploadMethod));
    } finally {
      setVerifying(false);
    }
  };

  const handleMethodSelect = (method: UploadMethod) => {
    setUploadMethod(method);
    setError(null);
  };

  const handleFileInputChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await uploadSelectedFile(file);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const file = event.dataTransfer.files?.[0];
    if (!file) return;

    await uploadSelectedFile(file);
  };

  const actionRow = (
    <div
      className={`flex shrink-0 flex-wrap justify-end gap-6 ${
        headerVariant === "connector-step"
          ? // In the Add Connector dialog the actions stay in view when the
            // form outgrows the dialog (e.g. an error on a laptop screen).
            `sticky -bottom-8 z-10 -mb-8 bg-white pb-8 pt-4 ${
              serverOptions.length > 1 ? "mt-6" : ""
            }`
          : "mt-auto ml-auto"
      }`}
    >
      {onClose ? (
        <Button
          data-testid={domId("btn", `${basePurpose}-cancel`)}
          type="button"
          onClick={handleClose}
          disabled={busy}
          variant="unstyled"
          className="inline-flex h-10 min-w-[114px] items-center justify-center rounded-[8px] bg-[#ECEFF3] px-5 text-[14px] font-medium text-[#00152C] disabled:opacity-50"
        >
          {resolvedCancelLabel}
        </Button>
      ) : null}

      <Button
        data-testid={domId("btn", `${basePurpose}-submit`)}
        type="button"
        onClick={() => void submitCurrentState()}
        disabled={!canSubmit || busy}
        aria-busy={busy}
        data-loading={busy ? "true" : undefined}
        variant="unstyled"
        className="inline-flex h-10 min-w-[158px] items-center justify-center rounded-[8px] px-5 text-[14px] font-medium text-white disabled:cursor-not-allowed disabled:bg-[#E8EBED] disabled:text-[#B2BBC4]"
        style={
          canSubmit && !busy
            ? {
                background:
                  "linear-gradient(90deg, #713D98 0%, #3E3D98 100%)",
              }
            : undefined
        }
      >
        {resolvedSubmitLabel}
      </Button>
    </div>
  );

  const formContent = (
    <div className={headerVariant === "connector-step" ? "flex min-h-0 w-full flex-1 flex-col" : "w-full max-w-[850px]"}>
      {showHeader ? (
        <div className="mb-8">
          <h2
            className={
              headerVariant === "connector-step"
                ? "text-xl font-semibold leading-7 text-[#0B0809]"
                : "text-[28px] font-semibold leading-[1.4] text-[#0B0809]"
            }
          >
            {resolvedTitle}
          </h2>
          <p className="mt-1 text-[14px] leading-[1.4] text-[#444746]">
            {resolvedSubtitle}
          </p>
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-3">
        {methodCards.map((methodCard) => (
          <MethodCard
            key={methodCard.value}
            id={domId("rad", `${basePurpose}-method-${methodCard.value}`)}
            title={methodCard.title}
            description={methodCard.description}
            icon={methodCard.icon}
            selected={uploadMethod === methodCard.value}
            onClick={() => handleMethodSelect(methodCard.value)}
          />
        ))}
      </div>

      {/* In the Add Connector dialog the drop zone is the part that gives way:
          when the window is too short for the form plus an error, it shrinks
          (down to 120px, its illustration with it) so the dialog does not
          need a scrollbar (AIS-2673). The wrapper steps aside (contents) so
          the drop zone is itself an item of the form's column. Below lg the
          method cards stack and the dialog scrolls anyway, so the drop zone
          keeps its full height there instead of clipping its text. */}
      <div className={headerVariant === "connector-step" ? "contents" : "mt-6"}>
        <h3
          className={`text-[16px] font-medium leading-[1.4] text-[#0B0809] ${
            headerVariant === "connector-step" ? "mt-6" : ""
          }`}
        >
          {uploadMethod === "file"
            ? t("sections.uploadFile")
            : uploadMethod === "text"
              ? t("sections.addContent")
              : t("sections.importUrl")}
        </h3>

        {uploadMethod === "file" ? (
          <>
            <Input
              ref={fileInputRef}
              data-testid={domId("txt", `${basePurpose}-file-input`)}
              type="file"
              onChange={handleFileInputChange}
              className="hidden"
              accept=".json,.yaml,.yml,.pdf,.doc,.docx,.txt"
            />

            {selectedFile && uploadedFileUrl && !uploading ? (
              <div
                className="mt-2 flex h-[100px] w-full items-center justify-center rounded-[8px] border border-[#E8EBED] bg-[#EBF5FF] px-6 py-5"
                data-testid={domId("div", `${basePurpose}-file-selected`)}
              >
                <div className="relative flex items-center justify-center gap-[18px]">
                  <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[8px] border border-[#DCDFE3] bg-white p-4">
                    <UploadedFileStateIcon className="h-7 w-7 shrink-0" />
                  </div>
                  <Button
                    data-testid={domId("btn", `${basePurpose}-file-remove`)}
                    type="button"
                    variant="unstyled"
                    aria-label={t("actions.removeFile")}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRemoveSelectedFile();
                    }}
                    className="absolute left-[46px] top-[2px] flex items-center rounded-full bg-white p-[2px] text-[#444746]"
                  >
                    <svg
                      viewBox="0 0 5 5"
                      className="h-2 w-2"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4.5 0.5L0.5 4.5M0.5 0.5L4.5 4.5"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                  <div className="flex flex-col items-center justify-center gap-[3px]">
                    <p className="max-w-[320px] truncate text-center text-[14px] font-medium leading-[1.4] text-[#444746]">
                      {selectedFile.name}
                    </p>
                    <Button
                      data-testid={domId(
                        "btn",
                        `${basePurpose}-file-choose-another`,
                      )}
                      type="button"
                      variant="unstyled"
                      aria-describedby={error ? errorId : undefined}
                      onClick={(event) => {
                        event.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="text-center text-[10px] font-medium leading-[18px] text-[#B2BBC4] underline underline-offset-2"
                    >
                      {t("actions.chooseAnotherFile")}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                aria-invalid={error?.kind === "validation"}
                aria-describedby={error ? errorId : undefined}
                data-testid={domId("btn", `${basePurpose}-file-trigger`)}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                className={`mt-2 flex h-[187px] w-full flex-col items-center justify-center rounded-[8px] border-2 border-dashed px-10 py-4 text-center transition-colors ${
                  headerVariant === "connector-step" ? "shrink-0 lg:min-h-[120px] lg:shrink lg:overflow-hidden" : ""
                } ${
                  isDragging
                    ? "border-[#A855F7] bg-[#FBF5FF]"
                    : "border-[#DCDFE3] bg-white"
                }`}
              >
                <UploadDropIllustration />

                {uploading ? (
                  <div className="mt-6 flex items-center gap-3 text-[#6F43B5]">
                    <ValidatingLoaderIcon className="h-5 w-5 animate-[spin_1.1s_linear_infinite]" />
                    <span className="text-[14px] font-medium leading-[1.4]">
                      {t("states.uploading")}
                    </span>
                  </div>
                ) : (
                  <>
                    <p className="mt-2 text-[16px] font-medium leading-[1.4] text-[#444746]">
                      <span
                        className="bg-clip-text font-bold text-transparent"
                        style={{ backgroundImage: BRAND_GRADIENT }}
                      >
                        {t("labels.clickToUpload")}
                      </span>{" "}
                      {t("labels.orDragDrop")}
                    </p>
                    <p className="mt-2 max-w-[720px] text-[12px] leading-[1.4] text-[#8E9BA8]">
                      {t("labels.supportedFormats")}
                    </p>
                  </>
                )}
              </div>
            )}
          </>
        ) : null}

        {uploadMethod === "text" ? (
          <Textarea
            data-testid={domId("are", `${basePurpose}-text-content`)}
            aria-label={t("sections.addContent")}
            aria-invalid={error?.kind === "validation"}
            aria-describedby={error ? errorId : undefined}
            value={textContent}
            onChange={(event) => {
              setTextContent(event.target.value);
              if (error) setError(null);
            }}
            placeholder={t("placeholders.pasteContent")}
            className="mt-2 min-h-[100px] max-h-[40vh] rounded-[8px] border border-[#B2BBC4] px-3 py-2 text-[13px] leading-[1.4] text-[#00152C] placeholder:text-[#8E9BA8] focus-visible:border-[#A855F7] focus-visible:ring-2 focus-visible:ring-[#F3E8FF]"
          />
        ) : null}

        {uploadMethod === "url" ? (
          <div className="relative mt-2">
            <Input
              data-testid={domId("txt", `${basePurpose}-import-url`)}
              type="url"
              aria-label={t("sections.importUrl")}
              aria-invalid={error?.kind === "validation"}
              aria-describedby={error ? errorId : undefined}
              value={urlContent}
              onChange={(event) => {
                setUrlContent(event.target.value);
                if (error) setError(null);
              }}
              placeholder={t("placeholders.importUrl")}
              className="h-[56px] rounded-[8px] border border-[#B2BBC4] py-4 pl-6 pr-10 text-[14px] leading-[1.4] text-[#00152C] placeholder:text-[#8E9BA8] focus-visible:border-[#A855F7] focus-visible:ring-2 focus-visible:ring-[#F3E8FF]"
            />
            {urlContent ? (
              <Button
                data-testid={domId("btn", `${basePurpose}-import-url-clear`)}
                type="button"
                variant="unstyled"
                aria-label={t("actions.clearUrl")}
                onClick={() => {
                  setUrlContent("");
                  setError(null);
                }}
                className="absolute right-[10px] top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[8px] bg-white text-[#6D7E91]"
              >
                <X className="h-4 w-4" />
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {error ? (
        // The error can be closed instead of staying until the next file
        // (AIS-2673); after a failure the user simply acts again.
        <InlineStatus
          ref={errorRef}
          id={errorId}
          tabIndex={-1}
          purpose={`${basePurpose}-error`}
          status="error"
          className="mt-4 scroll-mb-24 focus:outline-none"
          onDismiss={() => setError(null)}
        >
          {errorMessages[error.code]}
        </InlineStatus>
      ) : null}

      <div className="mt-6 flex flex-1 flex-col gap-6">
        {serverOptions.length > 1 ? (
          <fieldset className="w-full min-w-0">
            <legend className="text-sm font-medium text-[#0B0809]">
              {t("labels.serverUrl")}
            </legend>
            <div className="mt-2">
              <FilterDropdown
                purpose={`${basePurpose}-server-url`}
                value={selectedServerUrl}
                options={serverOptions.map((option) => option.url)}
                onChange={(value) => {
                  setSelectedServerUrl(value);
                  setShowServerError(false);
                }}
                placeholder={t("placeholders.serverUrl")}
                width="100%"
                maxVisibleRows={3}
              />
            </div>
            {showServerError ? (
              <p className="mt-2 text-sm text-red-600" role="alert">
                {t("errors.selectServerUrl")}
              </p>
            ) : null}
          </fieldset>
        ) : null}
        {headerVariant === "connector-step" ? null : actionRow}
      </div>
      {/* A sticky element cannot leave its parent, so in the Add Connector
          dialog the actions sit after this block instead of inside it. */}
      {headerVariant === "connector-step" ? actionRow : null}
    </div>
  );

  if (asModal) {
    return (
      <div
        data-testid={domId("mdl", `${basePurpose}-modal`)}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6"
      >
        <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[28px] bg-white shadow-xl">
          <div className="border-b border-slate-200 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[24px] font-semibold text-[#111827]">
                  {resolvedTitle}
                </h2>
                <p className="mt-2 text-[15px] leading-7 text-[#4B5563]">
                  {resolvedSubtitle}
                </p>
              </div>
              <Button
                data-testid={domId("btn", `${basePurpose}-modal-close`)}
                type="button"
                variant="unstyled"
                onClick={handleClose}
                disabled={busy}
                className="rounded-full p-2 text-slate-400"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </Button>
            </div>
          </div>
          <div className="max-h-[calc(90vh-92px)] overflow-y-auto px-6 py-6">
            {formContent}
          </div>
        </div>
      </div>
    );
  }

  return formContent;
}
